/**
 * Platform request-context contract.
 *
 * Five visitor facts. CloudFront writes them as `x-edu-ctx-*` headers, and
 * those names stay inside our edge. The browser receives the same facts in
 * the `rv_ctx` cookie. There is no bootstrap document.
 *
 * Partner header and cookie names are a mapping on the client record. They
 * are not part of these field names. A cookie the browser can change is not
 * a partner-edge source.
 *
 * A value that fails its length or pattern is unknown. Unknown stays the
 * string "unknown".
 *
 * `rv_ctx` is written by our edge and is also writable by the browser. Its
 * values are personalization hints, never authorization.
 */

export const REQUEST_CONTEXT_UNKNOWN = 'unknown' as const;

/** Hyphenated UUID. */
export const REQUEST_CONTEXT_PARTNER_TRIP_ID_PATTERN =
  '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$';
export const REQUEST_CONTEXT_PARTNER_TRIP_ID_MAX_LENGTH = 36;

/**
 * Stored session words. Each partner record maps its own source values.
 */
export const REQUEST_CONTEXT_SESSION_STATE_PATTERN =
  '^(?:signed-in|signed-out)$';
export const REQUEST_CONTEXT_SESSION_STATE_MAX_LENGTH = 10;

/** Stored experiment arm. This origin keeps `rv`. */
export const REQUEST_CONTEXT_EXPERIMENT_PATTERN = '^rv$';
export const REQUEST_CONTEXT_EXPERIMENT_MAX_LENGTH = 2;

/** Cloudflare bot score, 1–99. The score is stored. There is no cutoff. */
export const REQUEST_CONTEXT_BOT_PATTERN = '^(?:[1-9]|[1-9][0-9])$';
export const REQUEST_CONTEXT_BOT_MAX_LENGTH = 2;

/**
 * OneTrust groups. A missing cookie stays unknown. OneTrust reads the
 * browser cookie and applies its own startup. A present cookie can still
 * list every group.
 */
export const REQUEST_CONTEXT_CONSENT_GROUPS = [
  'C0001',
  'C0002',
  'C0003',
  'C0004',
  'C0005',
] as const;
export const REQUEST_CONTEXT_CONSENT_PATTERN = '^(?:C000[1-5])(?:,C000[1-5])*$';
export const REQUEST_CONTEXT_CONSENT_MAX_LENGTH =
  REQUEST_CONTEXT_CONSENT_GROUPS.join(',').length;

export type RequestContextUnknown = typeof REQUEST_CONTEXT_UNKNOWN;

export type RequestContextKey =
  | 'partnerTripId'
  | 'sessionState'
  | 'experiment'
  | 'bot'
  | 'consent';

export interface RequestContextFieldRule {
  readonly maxLength: number | null;
  readonly pattern: string | null;
}

export interface RequestContextField {
  readonly key: RequestContextKey;
  readonly header: `x-edu-ctx-${string}`;
  /** Author path. A condition reads this, and sees "unknown" until a value arrives. */
  readonly contextPath: `context.request.${RequestContextKey}`;
  readonly unknownReadsAs: RequestContextUnknown;
  readonly maxLength: number | null;
  readonly pattern: string | null;
}

export const REQUEST_CONTEXT_FIELDS = [
  {
    key: 'partnerTripId',
    header: 'x-edu-ctx-partner-trip-id',
    contextPath: 'context.request.partnerTripId',
    unknownReadsAs: REQUEST_CONTEXT_UNKNOWN,
    maxLength: REQUEST_CONTEXT_PARTNER_TRIP_ID_MAX_LENGTH,
    pattern: REQUEST_CONTEXT_PARTNER_TRIP_ID_PATTERN,
  },
  {
    key: 'sessionState',
    header: 'x-edu-ctx-session-state',
    contextPath: 'context.request.sessionState',
    unknownReadsAs: REQUEST_CONTEXT_UNKNOWN,
    maxLength: REQUEST_CONTEXT_SESSION_STATE_MAX_LENGTH,
    pattern: REQUEST_CONTEXT_SESSION_STATE_PATTERN,
  },
  {
    key: 'experiment',
    header: 'x-edu-ctx-experiment',
    contextPath: 'context.request.experiment',
    unknownReadsAs: REQUEST_CONTEXT_UNKNOWN,
    maxLength: REQUEST_CONTEXT_EXPERIMENT_MAX_LENGTH,
    pattern: REQUEST_CONTEXT_EXPERIMENT_PATTERN,
  },
  {
    key: 'bot',
    header: 'x-edu-ctx-bot',
    contextPath: 'context.request.bot',
    unknownReadsAs: REQUEST_CONTEXT_UNKNOWN,
    maxLength: REQUEST_CONTEXT_BOT_MAX_LENGTH,
    pattern: REQUEST_CONTEXT_BOT_PATTERN,
  },
  {
    key: 'consent',
    header: 'x-edu-ctx-consent',
    contextPath: 'context.request.consent',
    unknownReadsAs: REQUEST_CONTEXT_UNKNOWN,
    maxLength: REQUEST_CONTEXT_CONSENT_MAX_LENGTH,
    pattern: REQUEST_CONTEXT_CONSENT_PATTERN,
  },
] as const satisfies readonly RequestContextField[];

/** Author-facing list, derived from the field list so the two stay together. */
export const REQUEST_CONTEXT_AUTHOR_PATHS = REQUEST_CONTEXT_FIELDS.map(
  (field) => ({
    path: field.contextPath,
    unknownReadsAs: field.unknownReadsAs,
  }),
);

export type RequestContextSnapshot = Record<
  RequestContextKey,
  RequestContextUnknown | string
>;

export const UNKNOWN_REQUEST_CONTEXT: RequestContextSnapshot = {
  partnerTripId: REQUEST_CONTEXT_UNKNOWN,
  sessionState: REQUEST_CONTEXT_UNKNOWN,
  experiment: REQUEST_CONTEXT_UNKNOWN,
  bot: REQUEST_CONTEXT_UNKNOWN,
  consent: REQUEST_CONTEXT_UNKNOWN,
};

/**
 * Cookie the viewer-response function writes and the page reads at startup.
 * Partners see this name. They do not see `x-edu-ctx-*`.
 */
export const REQUEST_CONTEXT_COOKIE_NAME = 'rv_ctx' as const;

/** Cookie names a partner sees start with this prefix. */
export const REQUEST_CONTEXT_COOKIE_PREFIX = 'rv_' as const;

/** The cookie lives for the browser session. It has no max-age. */
export const REQUEST_CONTEXT_COOKIE_LIFETIME = 'session' as const;

/**
 * Attributes on the Set-Cookie our edge writes. Host-only means no Domain.
 * The page must read the cookie, so it is not HttpOnly. The value is
 * percent-encoded JSON.
 */
export const REQUEST_CONTEXT_COOKIE_ATTRIBUTES = {
  path: '/',
  secure: true,
  sameSite: 'Lax',
  httpOnly: false,
  hostOnly: true,
  encoding: 'uri',
} as const;

/**
 * Browsers drop a cookie around 4 KB. EDUN-18624 must stay inside this budget
 * when it writes `rv_ctx`. The count is the whole Set-Cookie we produce:
 * name, percent-encoded value, and attributes. An oversized field is replaced
 * with unknown so the other facts can still be stored.
 */
export const REQUEST_CONTEXT_COOKIE_MAX_BYTES = 4096;

/**
 * Working name for the sign-in session cookie. Sign-in still confirms it.
 * It is reserved here so a context cookie cannot take the name.
 */
export const REQUEST_CONTEXT_SESSION_COOKIE_WORKING_NAME =
  'rv_session' as const;

/**
 * Cookie-name fragment this contract does not reuse. `Optanon` is the
 * consent cookie OneTrust reads. Partner cookie names stay on the client
 * record.
 */
export const REQUEST_CONTEXT_REJECTED_COOKIE_MARKERS = ['Optanon'] as const;

const FIELD_BY_HEADER = new Map<string, RequestContextField>(
  REQUEST_CONTEXT_FIELDS.map((field) => [field.header, field]),
);

export function isRequestContextCookieNameAllowed(name: string): boolean {
  if (!name.startsWith(REQUEST_CONTEXT_COOKIE_PREFIX)) {
    return false;
  }
  return !REQUEST_CONTEXT_REJECTED_COOKIE_MARKERS.some((marker) =>
    name.includes(marker),
  );
}

export function readRequestContextValue(
  raw: unknown,
  rule: RequestContextFieldRule,
): RequestContextUnknown | string {
  if (typeof raw !== 'string') {
    return REQUEST_CONTEXT_UNKNOWN;
  }

  const value = raw.trim();
  if (value.length === 0) {
    return REQUEST_CONTEXT_UNKNOWN;
  }

  if (rule.maxLength !== null && value.length > rule.maxLength) {
    return REQUEST_CONTEXT_UNKNOWN;
  }

  if (rule.pattern !== null) {
    try {
      if (!new RegExp(rule.pattern).test(value)) {
        return REQUEST_CONTEXT_UNKNOWN;
      }
    } catch {
      return REQUEST_CONTEXT_UNKNOWN;
    }
  }

  return value;
}

/**
 * Builds a snapshot from canonical headers. Any other header is ignored.
 * A field with no published length or pattern is kept when it is a non-blank string.
 */
export function readRequestContextSnapshot(
  headers: Readonly<Record<string, unknown>>,
): RequestContextSnapshot {
  const snapshot: RequestContextSnapshot = { ...UNKNOWN_REQUEST_CONTEXT };

  for (const field of REQUEST_CONTEXT_FIELDS) {
    snapshot[field.key] = readRequestContextValue(headers[field.header], field);
  }

  return snapshot;
}

/**
 * The cookie value as stored. Percent-encoded JSON is decoded once. A value
 * that already starts with `{` is raw JSON and is not decoded again.
 */
function cookieJson(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith('{')) {
    return trimmed;
  }
  try {
    return decodeURIComponent(trimmed);
  } catch {
    return trimmed;
  }
}

/**
 * Reads the `rv_ctx` value. Only the five fields are kept. A missing,
 * blank, or unparseable cookie is an all-unknown snapshot.
 */
export function readRequestContextCookie(raw: unknown): RequestContextSnapshot {
  const snapshot: RequestContextSnapshot = { ...UNKNOWN_REQUEST_CONTEXT };
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return snapshot;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(cookieJson(raw));
  } catch {
    return snapshot;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return snapshot;
  }

  const record = parsed as Record<string, unknown>;
  for (const field of REQUEST_CONTEXT_FIELDS) {
    let rawValue = record[field.key];
    // Dual-read: cookies written before the partnerTripId rename.
    if (
      field.key === 'partnerTripId' &&
      (rawValue === undefined || rawValue === null) &&
      Object.prototype.hasOwnProperty.call(record, 'partnerVisitId')
    ) {
      rawValue = record.partnerVisitId;
    }
    snapshot[field.key] = readRequestContextValue(rawValue, field);
  }
  return snapshot;
}

function setCookieHeader(encodedValue: string): string {
  return `${REQUEST_CONTEXT_COOKIE_NAME}=${encodedValue}; Path=${REQUEST_CONTEXT_COOKIE_ATTRIBUTES.path}; Secure; SameSite=${REQUEST_CONTEXT_COOKIE_ATTRIBUTES.sameSite}`;
}

function utf8Bytes(value: string): number {
  let bytes = 0;
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (code <= 0x7f) bytes += 1;
    else if (code <= 0x7ff) bytes += 2;
    else if (code <= 0xffff) bytes += 3;
    else bytes += 4;
  }
  return bytes;
}

function setCookieBytes(snapshot: RequestContextSnapshot): number {
  return utf8Bytes(
    setCookieHeader(encodeURIComponent(JSON.stringify(snapshot))),
  );
}

/** The last equally long field is dropped first, so a short trip id is kept. */
function largestField(
  snapshot: RequestContextSnapshot,
): RequestContextKey | undefined {
  let largest: RequestContextKey | undefined;
  let largestLength = 0;
  for (const field of REQUEST_CONTEXT_FIELDS) {
    const value = snapshot[field.key];
    if (value === REQUEST_CONTEXT_UNKNOWN) continue;
    if (value.length >= largestLength) {
      largest = field.key;
      largestLength = value.length;
    }
  }
  return largest;
}

/**
 * Replaces oversized fields with unknown until the Set-Cookie fits. A value
 * the browser would drop is not kept.
 */
function fitRequestContextCookie(
  snapshot: RequestContextSnapshot,
): RequestContextSnapshot {
  const fitted: RequestContextSnapshot = { ...snapshot };
  for (let step = 0; step <= REQUEST_CONTEXT_FIELDS.length; step += 1) {
    if (setCookieBytes(fitted) <= REQUEST_CONTEXT_COOKIE_MAX_BYTES) {
      return fitted;
    }
    const key = largestField(fitted);
    if (!key) return fitted;
    fitted[key] = REQUEST_CONTEXT_UNKNOWN;
  }
  return fitted;
}

/**
 * Writes the `rv_ctx` value. The value lists the five fields, with an
 * explicit unknown for anything missing, invalid, or too large for the
 * cookie budget. Credentials and other keys are left out. The result is
 * percent-encoded so quotes, commas, semicolons, and non-ASCII stay inside
 * the cookie value.
 */
export function writeRequestContextCookie(
  value: Partial<RequestContextSnapshot> | null | undefined,
): string {
  const snapshot: RequestContextSnapshot = { ...UNKNOWN_REQUEST_CONTEXT };
  for (const field of REQUEST_CONTEXT_FIELDS) {
    snapshot[field.key] = readRequestContextValue(value?.[field.key], field);
  }
  return encodeURIComponent(JSON.stringify(fitRequestContextCookie(snapshot)));
}

/**
 * Set-Cookie for `rv_ctx`: Path=/, Secure, SameSite=Lax, host-only, session
 * lifetime, and not HttpOnly. No Domain and no Max-Age. The header stays
 * within `REQUEST_CONTEXT_COOKIE_MAX_BYTES`.
 */
export function formatRequestContextSetCookie(
  value: Partial<RequestContextSnapshot> | null | undefined,
): string {
  return setCookieHeader(writeRequestContextCookie(value));
}

export function requestContextFieldByHeader(
  header: string,
): RequestContextField | undefined {
  return FIELD_BY_HEADER.get(header);
}
